import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { MagnifyingGlass, MapPin, WifiHigh, Clock, Globe, Lightning, Link, Cpu, SpinnerGap, Funnel, Warning, CheckCircle, Lightbulb } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// Phase boundaries in seconds — aligned to actual worker pipeline timing.
// Subprocess hard-cap is 300s; pool window is 150s; post-processing adds ~30s.
const SEARCH_PHASES = [
  { until: 15,  label: "Expanding query with skill-graph AI",        detail: "Adding related keywords from your persona's job history" },
  { until: 45,  label: "Querying job APIs",                          detail: "Adzuna · Jooble · Himalayas · Remotive · Jobicy · Arbeitnow" },
  { until: 90,  label: "Scanning aggregators via JobSpy",            detail: "LinkedIn · Indeed · Glassdoor · Google Jobs" },
  { until: 150, label: "Deep-scraping India boards",                 detail: "Naukri · Instahyre · Hirist · Cutshort · IIMJobs" },
  { until: 210, label: "Crawling startup boards & meta-search",      detail: "Y Combinator · DuckDuckGo · stealth Playwright sessions" },
  { until: 260, label: "Deduplicating & filtering results",          detail: "Removing duplicates, US-only roles, and skeleton listings" },
  { until: 330, label: "Saving to database & AI-scoring",            detail: "Upserting new jobs and scoring against your resume" },
];

const SEARCH_TIPS = [
  "Each source returns up to 25 jobs — overlapping listings are merged automatically.",
  "Jobs you've already applied to or rejected are filtered out before they reach you.",
  "We use a skill-graph built from your job history to expand search terms with related keywords.",
  "Naukri, Instahyre, and YC use real browser automation with stealth mode to bypass bot detection.",
  "Cookie rotation prevents rate-limiting on aggressive sites like Naukri.",
  "Adzuna and Jooble are direct APIs — usually the fastest sources at ~15s each.",
  "If a source fails, the circuit breaker pauses it for 5 minutes so other sources keep working.",
  "AI scoring runs in the background after the search completes — scores will appear shortly after.",
  "Partial results are saved even if some sources time out — nothing is wasted.",
];

function formatElapsed(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

const INDIA_CITIES = [
  ["any", "Any City"],
  ["Gurugram", "Gurugram / Gurgaon"],
  ["Bengaluru", "Bengaluru / Bangalore"],
  ["Mumbai", "Mumbai"],
  ["Delhi NCR", "Delhi NCR"],
  ["Hyderabad", "Hyderabad"],
  ["Pune", "Pune"],
  ["Chennai", "Chennai"],
  ["Noida", "Noida"],
  ["Kolkata", "Kolkata"],
];

const US_CITIES = [["any", "Any City"], ["New York", "New York"], ["San Francisco", "San Francisco"], ["Austin", "Austin"], ["Seattle", "Seattle"], ["Remote", "Remote"]];
const UK_CITIES = [["any", "Any City"], ["London", "London"], ["Manchester", "Manchester"], ["Remote", "Remote"]];

const CITY_MAP = { India: INDIA_CITIES, USA: US_CITIES, UK: UK_CITIES, Canada: [["any", "Any City"], ["Toronto", "Toronto"], ["Vancouver", "Vancouver"]], Germany: [["any", "Any City"], ["Berlin", "Berlin"], ["Munich", "Munich"]] };

function SearchOverlay() {
  const [elapsed, setElapsed] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    const tickTimer = setInterval(() => setElapsed(e => e + 1), 1000);
    const tipTimer = setInterval(() => setTipIndex(i => (i + 1) % SEARCH_TIPS.length), 9000);
    return () => { clearInterval(tickTimer); clearInterval(tipTimer); };
  }, []);

  // Determine current phase by elapsed seconds.
  const currentPhaseIdx = SEARCH_PHASES.findIndex(p => elapsed < p.until);
  const phaseIdx = currentPhaseIdx === -1 ? SEARCH_PHASES.length - 1 : currentPhaseIdx;
  const phase = SEARCH_PHASES[phaseIdx];

  // Progress estimate: cap at 95% until completion to avoid the user feeling stuck at 100%.
  const expectedTotal = 240; // typical full-pipeline duration
  const pct = Math.min(95, Math.round((elapsed / expectedTotal) * 100));

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center overflow-y-auto" data-testid="search-overlay">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
           style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />

      <div className="relative w-full max-w-2xl px-6 py-10">
        {/* Header — keep tab open warning */}
        <div className="mb-8 flex items-start gap-3 p-4 rounded-lg bg-amber-500/[0.06] border border-amber-500/25">
          <Warning size={20} weight="duotone" className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-amber-300 mb-1">Keep this tab open</div>
            <div className="text-xs text-amber-400/70 leading-relaxed">
              Sankhya is querying 18+ job sources in parallel. This takes <span className="text-amber-300 font-medium">3 to 5 minutes</span> depending on which deep scrapers run for your filters. Closing the tab will stop the search.
            </div>
          </div>
        </div>

        {/* Spinner + elapsed */}
        <div className="flex items-center gap-5 mb-8">
          <div className="relative flex-shrink-0">
            <div className="w-16 h-16 rounded-full border-2 border-amber-500/20 flex items-center justify-center">
              <SpinnerGap size={32} className="text-amber-500 animate-spin" />
            </div>
            <div className="absolute -inset-3 rounded-full border border-amber-500/10 animate-ping" style={{ animationDuration: '2.4s' }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-['Outfit'] text-2xl font-semibold text-white mb-1">Searching for jobs</h2>
            <div className="flex items-baseline gap-3 text-xs">
              <span className="text-zinc-500">Elapsed</span>
              <span className="font-mono text-amber-400 text-base font-medium tabular-nums">{formatElapsed(elapsed)}</span>
              <span className="text-zinc-700">/ ~4:00 expected</span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Progress</span>
            <span className="text-xs font-mono text-zinc-400 tabular-nums">{pct}%</span>
          </div>
          <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-1000 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Phase list */}
        <div className="mb-8 space-y-2" data-testid="search-phases">
          {SEARCH_PHASES.map((p, i) => {
            const status = i < phaseIdx ? 'done' : i === phaseIdx ? 'active' : 'pending';
            return (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-md border transition-all ${
                status === 'active'
                  ? 'bg-amber-500/[0.06] border-amber-500/25'
                  : status === 'done'
                    ? 'bg-emerald-500/[0.03] border-emerald-500/15'
                    : 'bg-white/[0.01] border-white/5'
              }`}>
                <div className="flex-shrink-0 w-5 h-5 mt-0.5 flex items-center justify-center">
                  {status === 'done' && <CheckCircle size={16} weight="fill" className="text-emerald-400" />}
                  {status === 'active' && <SpinnerGap size={16} className="text-amber-400 animate-spin" />}
                  {status === 'pending' && <div className="w-1.5 h-1.5 rounded-full bg-white/15" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${
                    status === 'done' ? 'text-emerald-300/80' :
                    status === 'active' ? 'text-amber-300' :
                    'text-zinc-600'
                  }`}>
                    {p.label}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${status === 'pending' ? 'text-zinc-700' : 'text-zinc-500'}`}>
                    {p.detail}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Rotating tip */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
          <Lightbulb size={18} weight="duotone" className="text-sky-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1">Did you know</div>
            <div key={tipIndex} className="text-xs text-zinc-400 leading-relaxed animate-in fade-in duration-700">
              {SEARCH_TIPS[tipIndex]}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SearchPanel() {
  const { profile, loading, searchJobs, lastSearchId, lastSearchResult, scoreBatch, scrapeCareerPage, deepScrape, scoreAllUnscored } = useApp();
  const [searchText, setSearchText] = useState("");
  const [country, setCountry] = useState("India");
  const [city, setCity] = useState("any");
  const [workMode, setWorkMode] = useState("remote");
  const [remoteScope, setRemoteScope] = useState("india");
  const [jobType, setJobType] = useState("fulltime");
  const [resultsWanted, setResultsWanted] = useState("30");
  const [hoursOld, setHoursOld] = useState("72");
  // Restore last search result from context (survives refresh via localStorage)
  const [searchResult, setSearchResult] = useState(lastSearchResult);
  const [careerUrl, setCareerUrl] = useState("");
  const [scrapeResult, setScrapeResult] = useState(null);
  const [deepUrl, setDeepUrl] = useState("");
  const [deepResult, setDeepResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const cities = CITY_MAP[country] || [["any", "Any City"]];

  const handleSearch = async () => {
    if (!searchText.trim()) { toast.error("Enter a search description"); return; }
    setIsSearching(true);
    setSearchResult(null);

    try {
      const location = workMode === "remote"
        ? (remoteScope === "global" ? "Remote" : `Remote ${country}`)
        : (city ? `${city}, ${country}` : country);

      const result = await searchJobs({
        search_text: searchText,
        location,
        city: city === "any" ? "" : city,
        work_mode: workMode,
        remote_scope: remoteScope,
        job_type: jobType,
        sites: ["indeed", "linkedin"],
        results_wanted: parseInt(resultsWanted),
        hours_old: parseInt(hoursOld),
        use_resume: !!profile,
        country,
      });
      setSearchResult(result);
      if (result.status === "error") {
        const detail = result.error || "Unknown error. Check activity log.";
        toast.error(`Search failed: ${detail}`);
      } else if (result.status === "timeout") {
        toast.warning("Search is taking longer than expected. Check Results page shortly.");
      } else {
        const srcKeys = result.source_breakdown ? Object.keys(result.source_breakdown) : [];
        toast.success(`Found ${result.total} new jobs${srcKeys.length > 0 ? ` from ${srcKeys.length} sources` : ''}`);
      }
    } catch (e) {
      toast.error("Search failed. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleScoreBatch = async () => {
    if (!profile) { toast.error("Upload resume first"); return; }
    const sid = searchResult?.search_id || lastSearchId;
    if (!sid) return;
    try {
      const r = await scoreBatch(sid);
      toast.success(`Scored ${r.scored} jobs`);
    } catch (e) { toast.error("Scoring failed"); }
  };

  const handleScoreAll = async () => {
    try {
      const result = await scoreAllUnscored();
      if (result.unscored === 0) {
        toast.success("All jobs are already scored!");
      } else {
        toast.success(`Scoring ${result.unscored} unscored jobs in background...`);
      }
    } catch (e) { toast.error("Failed to trigger scoring"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px]" data-testid="search-panel">
      {isSearching && <SearchOverlay />}

      <div className="mb-8 animate-in">
        <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white">Search Engine</h1>
        <p className="text-sm text-zinc-500 mt-2">
          AI-powered search across 15+ job boards. Deduplicates, filters US-only roles, and auto-scores against your resume.
        </p>
      </div>

      {/* Search Input */}
      <div className="jp-card mb-5 animate-in delay-1" data-testid="search-input-container">
        <div className="jp-card-header">
          <div className="flex items-center gap-2">
            <MagnifyingGlass size={16} className="text-amber-500" weight="duotone" />
            <span className="jp-label">DESCRIBE YOUR IDEAL ROLE</span>
          </div>
          {profile && <span className="jp-badge jp-badge-emerald">Resume linked</span>}
        </div>
        <textarea
          data-testid="search-text-input"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="e.g., Product Owner for B2B SaaS platform, Agile/Scrum, stakeholder management, roadmapping..."
          className="w-full bg-transparent text-white text-sm p-5 border-none outline-none resize-none placeholder:text-zinc-700 leading-relaxed"
          rows={3}
        />
      </div>

      {/* Location & Work Mode — Structured */}
      <div className="jp-card mb-5 animate-in delay-2" data-testid="location-filters">
        <div className="jp-card-header">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-amber-500" weight="duotone" />
            <span className="jp-label">LOCATION & WORK MODEL</span>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {/* Work Mode Toggle */}
          <div>
            <div className="jp-label mb-2">WORK MODE</div>
            <div className="flex gap-2">
              {[
                { val: "remote", label: "Remote", desc: "Work from anywhere" },
                { val: "hybrid", label: "Hybrid", desc: "Office + Remote" },
                { val: "onsite", label: "On-site", desc: "Office only" },
              ].map(m => (
                <button key={m.val}
                  data-testid={`work-mode-${m.val}`}
                  onClick={() => { setWorkMode(m.val); if (m.val !== "remote") setRemoteScope("india"); }}
                  className={`flex-1 p-3 rounded-lg border text-center transition-all ${
                    workMode === m.val
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                      : 'border-white/8 bg-white/[0.02] text-zinc-500 hover:border-white/15'
                  }`}>
                  <div className="text-xs font-medium">{m.label}</div>
                  <div className="text-[10px] text-zinc-600 mt-0.5">{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Remote Scope (only when remote) */}
          {workMode === "remote" && (
            <div>
              <div className="jp-label mb-2">REMOTE SCOPE</div>
              <div className="flex gap-2">
                {[
                  { val: "india", label: "Remote — India", desc: "India-based remote roles" },
                  { val: "global", label: "Remote — Global", desc: "Worldwide / APAC / Anywhere" },
                ].map(s => (
                  <button key={s.val}
                    data-testid={`remote-scope-${s.val}`}
                    onClick={() => setRemoteScope(s.val)}
                    className={`flex-1 p-3 rounded-lg border text-center transition-all ${
                      remoteScope === s.val
                        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
                        : 'border-white/8 bg-white/[0.02] text-zinc-500 hover:border-white/15'
                    }`}>
                    <div className="text-xs font-medium">{s.label}</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">{s.desc}</div>
                  </button>
                ))}
              </div>
              {remoteScope === "global" && (
                <div className="mt-2 p-2 rounded-md bg-amber-500/5 border border-amber-500/15 text-[10px] text-amber-400/80">
                  <Funnel size={10} className="inline mr-1" />
                  US-only roles (requiring US citizenship / W2 / security clearance) are auto-filtered out.
                </div>
              )}
            </div>
          )}

          {/* Country + City (for onsite/hybrid, or country selector for remote-india) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="jp-label mb-2">COUNTRY</div>
              <Select value={country} onValueChange={(v) => { setCountry(v); setCity("any"); }}>
                <SelectTrigger className="bg-black/40 border-white/10 rounded-md h-9 text-xs" data-testid="filter-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md">
                  {[["India","India"],["USA","USA"],["UK","UK"],["Canada","Canada"],["Germany","Germany"]].map(([val, label]) => (
                    <SelectItem key={val} value={val} className="text-xs">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {workMode !== "remote" && (
              <div>
                <div className="jp-label mb-2">CITY</div>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger className="bg-black/40 border-white/10 rounded-md h-9 text-xs" data-testid="filter-city">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md">
                    {cities.map(([val, label]) => (
                      <SelectItem key={val} value={val} className="text-xs">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Other Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5 animate-in delay-3" data-testid="search-filters">
        {[
          { label: "JOB TYPE", icon: Globe, value: jobType, onChange: setJobType, id: "filter-job-type",
            options: [["fulltime","Full-time"],["parttime","Part-time"],["contract","Contract"],["internship","Internship"]] },
          { label: "RECENCY", icon: Clock, value: hoursOld, onChange: setHoursOld, id: "filter-recency",
            options: [["24","Last 24h"],["72","Last 3 days"],["168","Last week"],["720","Last month"]] },
          { label: "MAX RESULTS", icon: null, value: resultsWanted, onChange: setResultsWanted, id: "filter-results",
            options: [["15","15 per source"],["30","30 per source"],["50","50 per source"]] },
        ].map(f => (
          <div key={f.id} className="jp-card p-3">
            <div className="jp-label mb-2 flex items-center gap-1">
              {f.icon && <f.icon size={12} />} {f.label}
            </div>
            <Select value={f.value} onValueChange={f.onChange}>
              <SelectTrigger className="bg-black/40 border-white/10 rounded-md h-8 text-xs" data-testid={f.id}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md">
                {f.options.map(([val, label]) => (
                  <SelectItem key={val} value={val} className="text-xs">{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 mb-6 animate-in delay-4" data-testid="search-actions">
        <Button data-testid="search-submit-btn" onClick={handleSearch} disabled={loading.search || isSearching}
          className="bg-amber-500 text-black hover:bg-amber-400 rounded-md font-medium text-sm h-10 px-6">
          {loading.search || isSearching ? (
            <><SpinnerGap size={16} className="mr-2 animate-spin" /> Searching...</>
          ) : (
            <><MagnifyingGlass size={16} className="mr-2" /> Execute Search</>
          )}
        </Button>
        {profile && (
          <>
            {searchResult && (
              <Button data-testid="score-batch-btn" onClick={handleScoreBatch} disabled={loading.scoreBatch}
                className="bg-transparent border border-amber-500/30 text-amber-500 hover:bg-amber-500/10 rounded-md text-sm h-10 px-5">
                {loading.scoreBatch ? "Scoring..." : <><Lightning size={16} className="mr-2" weight="fill" /> Score This Batch</>}
              </Button>
            )}
            <Button data-testid="score-all-unscored-btn" onClick={handleScoreAll} disabled={loading.scoreAll}
              className="bg-transparent border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 rounded-md text-sm h-10 px-5">
              {loading.scoreAll ? "Triggering..." : <><Lightning size={16} className="mr-2" weight="fill" /> Score All Unscored</>}
            </Button>
          </>
        )}
      </div>

      {/* Result Summary */}
      {searchResult && (
        <div className="jp-card animate-in" data-testid="search-result-summary">
          <div className="jp-card-header">
            <span className="jp-label">SEARCH COMPLETE</span>
            <div className="flex items-center gap-2">
              <span className="jp-badge jp-badge-amber">{searchResult.total} new jobs</span>
              {searchResult.auto_scoring && (
                <span className="jp-badge jp-badge-emerald flex items-center gap-1">
                  <SpinnerGap size={10} className="animate-spin" /> Auto-scoring
                </span>
              )}
            </div>
          </div>
          <div className="p-5">
            <div className="jp-label mb-2">AI-EXPANDED SEARCH TERMS</div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {(searchResult.search_terms || []).map((term, i) => (
                <span key={i} className="jp-badge jp-badge-sky">{term}</span>
              ))}
            </div>
            {searchResult.source_breakdown && Object.keys(searchResult.source_breakdown).length > 0 && (
              <div className="mb-4">
                <div className="jp-label mb-2">SOURCES THAT RETURNED RESULTS</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(searchResult.source_breakdown).sort(([,a],[,b]) => b - a).map(([src, count]) => (
                    <span key={src} className="jp-badge jp-badge-amber capitalize">{src}: {count}</span>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-zinc-600">Duplicates merged. Applied/rejected jobs excluded. Switch to Results to view & score.</p>
          </div>
        </div>
      )}

      {/* Career Page Scraper */}
      <div className="jp-card mt-6 animate-in delay-5" data-testid="career-page-scraper">
        <div className="jp-card-header">
          <div className="flex items-center gap-2">
            <Link size={16} className="text-amber-500" weight="duotone" />
            <span className="jp-label">CAREER PAGE SCRAPER</span>
          </div>
        </div>
        <div className="p-5">
          <p className="text-xs text-zinc-500 mb-3">Paste a company's career page URL. AI extracts all job listings.</p>
          <div className="flex gap-3">
            <input
              data-testid="career-url-input" type="url" value={careerUrl}
              onChange={(e) => setCareerUrl(e.target.value)}
              placeholder="https://company.com/careers"
              className="flex-1 bg-black/40 border border-white/10 rounded-md h-10 px-4 text-sm text-white placeholder:text-zinc-700 outline-none focus:border-amber-500/40"
            />
            <Button data-testid="scrape-career-btn"
              onClick={async () => {
                if (!careerUrl) { toast.error("Enter a URL"); return; }
                try {
                  const result = await scrapeCareerPage(careerUrl);
                  setScrapeResult(result);
                  toast.success(`Found ${result.jobs_found || 0} jobs`);
                } catch (e) { toast.error("Scraping failed"); }
              }}
              disabled={loading.scrape}
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-md text-sm h-10 px-5 font-medium">
              {loading.scrape ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Scraping...</> : "Scrape"}
            </Button>
          </div>
          {scrapeResult && (
            <div className="mt-4 p-3 rounded-md bg-white/[0.02] border border-white/5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-400 font-medium">{scrapeResult.company || "Company"}</span>
                <span className="jp-badge jp-badge-emerald">{scrapeResult.jobs_found || 0} jobs</span>
              </div>
              {(scrapeResult.jobs || []).slice(0, 5).map((j, i) => (
                <div key={i} className="text-xs text-zinc-500 py-1.5 border-t border-white/5">
                  {j.title} {j.location ? `| ${j.location}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Deep Scraper */}
      <div className="jp-card mt-6 animate-in delay-6" data-testid="deep-scraper">
        <div className="jp-card-header">
          <div className="flex items-center gap-2">
            <Cpu size={16} className="text-amber-500" weight="duotone" />
            <span className="jp-label">DEEP SCRAPER</span>
          </div>
        </div>
        <div className="p-5">
          <p className="text-xs text-zinc-500 mb-3">Crawl4AI async deep scrape any URL.</p>
          <div className="flex gap-3">
            <input
              data-testid="deep-url-input" type="url" value={deepUrl}
              onChange={(e) => setDeepUrl(e.target.value)}
              placeholder="https://linkedin.com/jobs/... or https://naukri.com/..."
              className="flex-1 bg-black/40 border border-white/10 rounded-md h-10 px-4 text-sm text-white placeholder:text-zinc-700 outline-none focus:border-amber-500/40"
            />
            <Button data-testid="deep-scrape-btn"
              onClick={async () => {
                if (!deepUrl) { toast.error("Enter a URL"); return; }
                try {
                  const result = await deepScrape(deepUrl);
                  setDeepResult(result);
                  toast.success(`Extracted ${result.jobs_found || 0} jobs`);
                } catch (e) { toast.error("Deep scrape failed"); }
              }}
              disabled={loading.deepScrape}
              className="bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:bg-amber-500/30 rounded-md text-sm h-10 px-5 font-medium">
              {loading.deepScrape ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Crawling...</> : <><Cpu size={15} className="mr-1.5" /> Deep Scrape</>}
            </Button>
          </div>
          {deepResult && (
            <div className="mt-4 p-3 rounded-md bg-white/[0.02] border border-white/5">
              <span className="jp-badge jp-badge-emerald">{deepResult.jobs_found || 0} jobs</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
